import { useState } from 'react'
import { useD1Tables } from '../hooks/useD1Browser'
import { Card } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import {
  Database, ChevronRight, ChevronDown,
} from 'lucide-react'
import { SeedRowDetail } from '@/components/SeedDataViewer'
import { D1FilteredTable } from '@/components/D1FilteredTable'

function TableCard({ name, rowCount, db = 'seeds' }: { name: string; rowCount: number; db?: string }) {
  const [expanded, setExpanded] = useState(false)
  const isSeedsTable = name === 'seeds' && db === 'seeds'

  return (
    <Card className="overflow-hidden">
      <button
        className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-muted/30 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded
          ? <ChevronDown className="size-4 text-muted-foreground shrink-0" />
          : <ChevronRight className="size-4 text-muted-foreground shrink-0" />
        }
        <Database className="size-4 text-muted-foreground shrink-0" />
        <div className="flex-1 min-w-0">
          <span className="text-sm font-medium font-mono text-foreground">{name}</span>
        </div>
        <Badge variant="secondary" className="text-xs shrink-0">
          {rowCount} {rowCount === 1 ? 'row' : 'rows'}
        </Badge>
      </button>

      {expanded && (
        <D1FilteredTable
          table={name}
          db={db}
          customRowDetail={isSeedsTable ? (row) => <SeedRowDetail row={row} /> : undefined}
        />
      )}
    </Card>
  )
}

function D1DatabaseView({ db }: { db: string }) {
  const { tables, loading, error } = useD1Tables(db)
  const totalRows = tables.reduce((sum, t) => sum + t.rowCount, 0)

  if (error) {
    return <div className="text-sm text-destructive">Failed to load D1 data: {error}</div>
  }

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4].map(i => <Skeleton key={i} className="h-14 w-full" />)}
      </div>
    )
  }

  if (tables.length === 0) {
    return <div className="text-sm text-muted-foreground">No tables found.</div>
  }

  return (
    <>
      <p className="mb-4 text-sm text-muted-foreground">
        {tables.length} tables, {totalRows} total rows
      </p>
      <div className="space-y-3">
        {tables.map(t => (
          <TableCard key={t.name} name={t.name} rowCount={t.rowCount} db={db} />
        ))}
      </div>
    </>
  )
}

export function D1BrowserPage() {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-foreground">Database Browser</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Inspect data stored in D1 (SQLite), organized by table.
        </p>
      </div>

      <Tabs defaultValue="seeds">
        <TabsList>
          <TabsTrigger value="seeds">Seeds DB</TabsTrigger>
          <TabsTrigger value="geo">Geo DB</TabsTrigger>
        </TabsList>
        <TabsContent value="seeds" className="mt-6">
          <D1DatabaseView db="seeds" />
        </TabsContent>
        <TabsContent value="geo" className="mt-6">
          <D1DatabaseView db="geo" />
        </TabsContent>
      </Tabs>
    </div>
  )
}
